import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  MotifModal,
  MotifModalHeader,
  MotifModalBody,
  MotifModalFooter,
  MotifFormField,
  MotifSelect,
  MotifOption,
  MotifInput,
  MotifFileUploader,
  MotifFileUploaderItem,
  MotifIcon,
  MotifProgressLoader,
} from "@ey-xd/motif-react";
import { actionIcDescription24px } from "@ey-xd/motif-react/assets/icons";
import "./UploadEditCreditorDoc.scss";
import ConfirmationModal from "@/components/Modal/ConfirmationModal/ConfirmationModal";
import { useCase } from "@/features/case/use-case";
import {
  useLazyGetDocumentTypesQuery,
  useUploadDocumentMutation,
  useUpdateDocumentMutation,
  fileExtensions,
  useLazyDownloadDocumentQuery,
} from "@/services/documents/documentsReducer";
import Button from "@/components/Form/Button/Button";
import Label from "@/components/Form/Label/Label";
import { GetCommonDownloadImgButton } from "@/components/Images/CommonImages";
import NotificationGrid from "@/components/Notification/NotificationGrid";

export default function UploadEditCreditorDoc({
  show,
  setShow,
  creditorId,
  isEdit,
  document,
}: {
  show: boolean;
  setShow: (isVisible: boolean) => void;
  creditorId: string;
  isEdit: boolean;
  document: any;
}) {
  const { t } = useTranslation();
  const caseData = useCase();
  const { caseId } = caseData;
  const [isUploading, setIsUploading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [showDiscardMessage, setShowDiscardMessage] = useState(false);
  const [documentType, setDocumentType] = useState("");
  const [comment, setComment] = useState("");
  const [fileName, setFileName] = useState("");
  const [documentTypes, setDocumentTypes] = useState([]);
  const [getDocumentTypesQuery] = useLazyGetDocumentTypesQuery();
  const [uploadDocument] = useUploadDocumentMutation();
  const [updateDocument] = useUpdateDocumentMutation();
  const [errorMessage, setErrorMessage] = useState("");

  const [downloadDocument, { isLoading: downloadLoading }] =
    useLazyDownloadDocumentQuery();

  const handleDownload = (documentData: any) => {
    setErrorMessage("");
    let fileExtensionName = documentData?.fileName?.split(".");
    let documentName = "";
    if (fileExtensionName.length > 1 && documentData?.displayName) {
      documentName = `${documentData.displayName}.${
        fileExtensionName[fileExtensionName?.length - 1]
      }`;
    } else {
      documentName = documentData.fileName;
    }
    downloadDocument({
      caseId: caseId,
      documentId: documentData.id,
      fileName: documentName,
    })
      .then((response: any) => {
        if (response?.error && "data" in response.error) {
          try {
            const errObj = JSON.parse(response?.error?.data as string);
            setErrorMessage(errObj?.message || errObj.title);
          } catch (error) {
            setErrorMessage("Something went wrong!!");
            console.error("File download error:", error);
          }
        }
      })
      .catch((error: any) => {
        setErrorMessage("Something went wrong!!");
        console.error("File download error:", error);
      });
  };
  useEffect(() => {
    getDocumentTypesQuery().then((response) => {
      if (response.data) {
        setDocumentTypes(response.data);
      }
    });
    // eslint-disable-next-line
  }, []);

  let [editFormData, setEditFormData] = useState({
    fileName: "",
    documentType: "",
    comment: "",
    file: new File([""], ""),
  });

  useEffect(() => {
    if (isEdit) {
      const type: any = documentTypes.filter((x: any) => {
        return x.name === document.documentType;
      });
      setFileName(document.displayName);
      setDocumentType(type.length !== 0 ? type[0].id : "");
      setComment(document.comment);
      setFile(new File([""], document.fileName));
      setEditFormData({
        fileName: document.displayName,
        documentType: type.length !== 0 ? type[0].id : "",
        comment: document.comment,
        file: new File([""], document.fileName),
      });
    }
    // eslint-disable-next-line
  }, [documentTypes]);

  const handleFileChange = (files: any) => {
    const file = files[0];
    if (file) {
      let displayFileName = file.name.slice(0, file.name.lastIndexOf("."));
      if (displayFileName.length > 100) {
        displayFileName = displayFileName.slice(0, 100);
      }
      setFileName(displayFileName);
      setFile(file);
    }
  };

  const handleSubmit = useCallback(() => {
    const formData = new FormData();
    setIsUploading(true);
    formData.append("CaseId", caseId);
    formData.append("DisplayName", fileName);
    formData.append("Comment", comment);
    formData.append("DocumentTypeId", documentType);

    if (isEdit) {
      formData.append("DocumentId", document.id);
      if (file && file.size !== 0) {
        formData.append("file", file);
      }
      updateDocument(formData).then(() => {
        setIsUploading(false);
        setShow(false);
      });
    } else {
      formData.append("EntityId", creditorId);
      formData.append("EntityType", "1");

      if (file) {
        formData.append("file", file);
      }
      uploadDocument(formData).then(() => {
        setIsUploading(false);
        setShow(false);
      });
    }
  }, [
    caseId,
    fileName,
    comment,
    documentType,
    isEdit,
    document.id,
    file,
    updateDocument,
    setShow,
    creditorId,
    uploadDocument,
  ]);

  let discardtrig = false;
  if (
    (file !== null ||
      fileName !== "" ||
      documentType !== "" ||
      comment !== "") &&
    isEdit === false
  ) {
    discardtrig = true;
  } else if (isEdit) {
    if (
      !(
        JSON.stringify(file) === JSON.stringify(editFormData.file) &&
        fileName === editFormData.fileName &&
        documentType === editFormData.documentType &&
        comment === editFormData.comment
      )
    ) {
      discardtrig = true;
    }
  }
  function charLimitIndicator(value: string) {
    return (
      <div className="display-flex justify-content-end">
        <div className='motif-body2-default-regular title'>{value?.length || 0}/100</div>
      </div>
    );
  }

  return (
    <>
      {errorMessage && (
        <div className="pad-b-10">
          <NotificationGrid
            show={true}
            type="error"
            message={errorMessage}
            scrollToTop={false}
          />
        </div>
      )}
      <MotifModal
        className="upload-edit-document-container"
        show={show}
        onClose={() => {
          if (discardtrig) {
            setShowDiscardMessage(true);
          } else {
            setShow(false);
          }
        }}
        focusTrapOptions={{
          tabbableOptions: {
            displayCheck: "none",
          },
        }}
      >
        <MotifModalHeader>
          {isEdit
            ? t(
                "pages.creditors.creditorDocumentUpload.documentPopup.editTitle"
              )
            : t("pages.creditors.creditorDocumentUpload.documentPopup.title")}
        </MotifModalHeader>
        <MotifModalBody>
          {documentTypes.length === 0 ? (
            <MotifProgressLoader show variant="default" />
          ) : (
            <>
              {t(
                "pages.creditors.creditorDocumentUpload.documentPopup.subtitle"
              )}
              <div className="motif-row document-inputs">
                <div className="motif-col-md-2 motif-col-lg-4">
                  <Label
                    title={
                      "pages.creditors.creditorDocumentUpload.documentPopup.fileName"
                    }
                    required={true}
                  />
                  <MotifFormField aria-label="Input" role="group">
                    <MotifInput
                      // @ts-ignore
                      onChange={(e) => setFileName(e.target.value)}
                      value={fileName}
                      maxLength={100}
                    />
                    {charLimitIndicator(fileName)}
                  </MotifFormField>
                </div>
                <div className="motif-col-md-2 motif-col-lg-4">
                  <MotifFormField>
                    <Label
                      title={
                        "pages.creditors.creditorDocumentUpload.documentPopup.documentType"
                      }
                      required={false}
                    />
                    <MotifSelect
                      onChange={(value) => setDocumentType(value)}
                      value={documentType}
                      visibleOptions={3}
                    >
                      {documentTypes.map((option: any) => {
                        return (
                          <MotifOption value={option.id} key={option.id}>
                            {option.name}
                          </MotifOption>
                        );
                      })}
                    </MotifSelect>
                  </MotifFormField>
                </div>
                <div className="motif-col-md-2 motif-col-lg-4">
                  <Label
                    title={
                      "pages.creditors.creditorDocumentUpload.documentPopup.comment"
                    }
                  />
                  <MotifFormField aria-label="Input" role="group">
                    <MotifInput
                      // @ts-ignore
                      onChange={(e) => setComment(e.target.value)}
                      value={comment}
                      maxLength={100}
                    />
                    {charLimitIndicator(comment)}
                  </MotifFormField>
                </div>
              </div>

              {document?.id ? (
                file && (
                  <>
                    <br></br>
                    <MotifFileUploaderItem
                      fileIcon={<MotifIcon src={actionIcDescription24px} />}
                      fileName={file.name}
                      uploaded={false}
                      customButton={
                        document?.id && (
                          <GetCommonDownloadImgButton
                            loading={downloadLoading}
                            onClick={() => {
                              handleDownload(document);
                            }}
                          />
                        )
                      }
                      uploadedText={
                        t(
                          "pages.creditors.creditorDocumentUpload.documentPopup.displayName"
                        ) + fileName
                      }
                      error={
                        (file && file.size >= fileExtensions.maxFileSize) ||
                        false
                      }
                      errorText={t(
                        "pages.creditors.creditorDocumentUpload.stageStatusPopup.filesizeExceedsLimit",
                        {
                          filesize: "250",
                        }
                      )}
                    />
                  </>
                )
              ) : (
                <div className="document-uploader">
                  <MotifFileUploader
                    label={t(
                      "pages.creditors.creditorDocumentUpload.documentPopup.uploadText"
                    )}
                    labelDescription={t(
                      "pages.creditors.creditorDocumentUpload.documentPopup.uploadDescription"
                    )}
                    onDrop={(files) => handleFileChange(files)}
                    maxFiles={1}
                    disabled={isEdit}
                    accept={fileExtensions.default}
                  >
                    {file && (
                      <MotifFileUploaderItem
                        fileIcon={<MotifIcon src={actionIcDescription24px} />}
                        fileName={file.name}
                        uploaded={document?.id ? false : true}
                        customButton={
                          document?.id && (
                            <GetCommonDownloadImgButton
                              loading={downloadLoading}
                              onClick={() => {
                                handleDownload(document);
                              }}
                            />
                          )
                        }
                        uploadedText={
                          t(
                            "pages.creditors.creditorDocumentUpload.documentPopup.displayName"
                          ) + fileName
                        }
                        error={
                          (file && file.size >= fileExtensions.maxFileSize) ||
                          false
                        }
                        errorText={t(
                          "pages.creditors.creditorDocumentUpload.stageStatusPopup.filesizeExceedsLimit",
                          {
                            filesize: "250",
                          }
                        )}
                      />
                    )}
                  </MotifFileUploader>
                </div>
              )}
            </>
          )}
        </MotifModalBody>
        <MotifModalFooter>
          <div className="document-footer">
            <Button
              variant="secondary"
              onClickHandler={() => {
                if (discardtrig) {
                  setShowDiscardMessage(true);
                } else {
                  setShow(false);
                }
              }}
              label={t(
                "pages.creditors.creditorDocumentUpload.documentPopup.cancel"
              )}
            />
            <Button
              type={isUploading ? "loader" : "submit"}
              variant="primary"
              onClickHandler={handleSubmit}
              label={
                isEdit
                  ? t(
                      "pages.creditors.creditorDocumentUpload.documentPopup.save"
                    )
                  : t(
                      "pages.creditors.creditorDocumentUpload.documentPopup.upload"
                    )
              }
              disabled={!file || !fileName || isUploading}
            />
          </div>
        </MotifModalFooter>
      </MotifModal>

      <ConfirmationModal
        isVisible={showDiscardMessage}
        setIsVisible={setShowDiscardMessage}
        cancelBtnApproveHandler={() => setShow(false)}
        cancelBtnRejectHandler={() => setShowDiscardMessage(false)}
      />
    </>
  );
}
